import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

export const MARKET_REFRESH_MS = 15000;

export const resolveAssetUrl = (url) => {
  if (!url || url === 'none') return url;
  if (/^https?:\/\//i.test(url)) return url;

  const apiBase = API.defaults.baseURL || '';
  const apiOrigin = apiBase.replace(/\/api\/?$/, '');

  if (url.startsWith('/uploads/')) {
    return `${apiBase}${url}`;
  }

  if (url.startsWith('/')) {
    return `${apiOrigin}${url}`;
  }

  return `${apiOrigin}/${url}`;
};

API.interceptors.request.use(config => {
  const token = localStorage.getItem('ipl_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const authAPI = {
  login:           (data) => API.post('/auth/login', data),
  register:        (data) => API.post('/auth/register', data),
  googleSignIn:    (credential)              => API.post('/auth/google', { credential }),
  me:              ()     => API.get('/auth/me'),
  forgotPassword:  (email)                   => API.post('/auth/forgot-password', { email }),
  resetPassword:   (email, otp, newPassword) => API.post('/auth/reset-password', { email, otp, newPassword }),
  sendLoginOTP:    (phone)      => API.post('/auth/send-login-otp', { phone }),
  verifyLoginOTP:  (phone, otp) => API.post('/auth/verify-login-otp', { phone, otp }),
};

export const playersAPI = {
  getAll:       () => API.get('/players'),
  getOne:       (id) => API.get(`/players/${id}`),
  getPortfolio: () => API.get('/players/portfolio/mine'),
};

export const tradesAPI = {
  buy:          (player_id) => API.post('/trades/buy', { player_id }),
  sell:         (player_id) => API.post('/trades/sell', { player_id }),
  history:      () => API.get('/trades/history'),
  marketStatus: () => API.get('/trades/market-status'),
};

export const bucketAPI = {
  get:     ()         => API.get('/bucket'),
  add:     (player_id, type) => API.post('/bucket/add', { player_id, type }),
  remove:  (id)       => API.delete(`/bucket/${id}`),
  clear:   ()         => API.delete('/bucket/clear/all'),
  execute: ()         => API.post('/bucket/execute'),
};

export const leaderboardAPI = {
  get: () => API.get('/leaderboard'),
};

export const contestsAPI = {
  create:      (data)        => API.post('/contests/create', data),
  join:        (invite_code) => API.post('/contests/join', { invite_code }),
  my:          ()            => API.get('/contests/my'),
  detail:      (id)          => API.get(`/contests/${id}`),
  leaderboard: (id)          => API.get(`/contests/${id}/leaderboard`),
  portfolio:   (id)          => API.get(`/contests/${id}/portfolio`),
  buy:         (id, player_id) => API.post(`/contests/${id}/buy`, { player_id }),
  sell:        (id, player_id) => API.post(`/contests/${id}/sell`, { player_id }),
  history:     (id)          => API.get(`/contests/${id}/history`),
  bucket:      (id)          => API.get(`/contests/${id}/bucket`),
  addToBucket: (id, player_id, type) => API.post(`/contests/${id}/bucket/add`, { player_id, type }),
  removeFromBucket: (id, orderId) => API.delete(`/contests/${id}/bucket/${orderId}`),
  clearBucket: (id)          => API.delete(`/contests/${id}/bucket/clear/all`),
  executeBucket: (id)        => API.post(`/contests/${id}/bucket/execute`),
};

export const feedAPI = {
  all:        () => API.get('/feed/all'),
  news:       () => API.get('/feed/news'),
  highlights: () => API.get('/feed/highlights'),
};

export const friendsAPI = {
  list:         ()         => API.get('/friends'),
  requests:     ()         => API.get('/friends/requests'),
  sent:         ()         => API.get('/friends/sent'),
  search:       (q)        => API.get(`/friends/search?q=${encodeURIComponent(q)}`),
  sendRequest:  (userId)   => API.post(`/friends/request/${userId}`),
  accept:       (userId)   => API.post(`/friends/accept/${userId}`),
  decline:      (userId)   => API.post(`/friends/decline/${userId}`),
  remove:       (userId)   => API.delete(`/friends/${userId}`),
  getChat:      (userId)   => API.get(`/friends/chat/${userId}`),
  sendMessage:  (userId, content) => API.post(`/friends/chat/${userId}`, { content }),
  markRead:     (userId)   => API.put(`/friends/chat/${userId}/read`),
};

export const matchesAPI = {
  list: () => API.get('/matches'),
  live: () => API.get('/matches/live'),
  get:  (id) => API.get(`/matches/${id}`),
};

export const intelligenceAPI = {
  schedule: () => API.get('/intelligence/schedule'),
  predict:  (team1, team2, venue, matchNo, date) => {
    const params = new URLSearchParams({ team1, team2 });
    if (venue)   params.set('venue',   venue);
    if (matchNo) params.set('matchNo', matchNo);
    if (date)    params.set('date',    date);
    return API.get(`/intelligence/predict?${params}`);
  },
};

export const donationsAPI = {
  recent: ()         => API.get('/donations/recent'),
  create: (data)     => API.post('/donations/create', data),
  verify: (data)     => API.post('/donations/verify', data),
};

export const profileAPI = {
  get:           ()     => API.get('/profile'),
  update:        (data) => API.put('/profile', data),
  uploadAvatar:  (formData) => API.post('/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  markRulesSeen: ()     => API.put('/profile/rules-seen'),
  notifications: ()     => API.get('/profile/notifications'),
  markAllRead:   ()     => API.put('/profile/notifications/read'),
};

export default API;
